import { IErmDevOpsApiClientServiceConfig } from './erm-devops-api-client.service-config';
import { ErmApiClientBuilderService } from '../erm-api-client-builder.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { GetAlarmsResponse } from './responses/GetAlarmsResponse';
import { GetCloudAccountsResponse } from './responses/GetCloudAccountsResponse';
import { GetCloudAccountResponse } from './responses/GetCloudAccountResponse';
import { GetCloudCustomersResponse } from './responses/GetCloudCustomersResponse';
import { GetCloudCustomerResponse } from './responses/GetCloudCustomerResponse';
import { GetCloudEnvironmentsResponse } from './responses/GetCloudEnvironmentsResponse';
import { GetCloudEnvironmentResponse } from './responses/GetCloudEnvironmentResponse';
import { GetRegionUsageResponse } from './responses/GetRegionUsageResponse';
import { GetCloudEnvironmentAlarmsResponse } from './responses/GetCloudEnvironmentAlarmsResponse';
import { GetCloudEnvironmentHealthCheckResultResponse } from './responses/GetCloudEnvironmentHealthCheckResultResponse';
import { GetCloudEnvironmentHealthChecksReponse } from './responses/GetCloudEnvironmentHealthChecksReponse';
import { GetCloudAccountAlarmsResponse } from './responses/GetCloudAccountAlarmsResponse';
import { GetFleetDefinitionsResponse } from './responses/GetFleetDefinitionsResponse';
import { GetFleetConfigurationsResponse } from './responses/GetFleetConfigurationsResponse';
import { CreateFleetDefinitionResponse } from './responses/CreateFleetDefinitionResponse';
import { UpdateFleetConfigurationRequest } from './request/UpdateFleetConfigurationRequest';
import { UpdateFleetConfigurationResponse } from './responses/UpdateFleetConfigurationResponse';
import { CreateFleetDefinitionRequest } from './request/CreateFleetDefinitionRequest';
import { DeleteFleetDefinitionRequest } from './request/DeleteFleetDefinitionRequest';
import { GetFleetDefinitionDefaultsResponse } from './responses/GetFleetDefinitionDefaultsResponse';
import { DeleteFleetDefinitionResponse } from './responses/DeleteFleetDefinitionResponse';
import { GetFleetConstantClassCollectionResponse } from './responses/GetFleetConstantClassCollectionResponse';
import { DeleteFleetConfigurationRequest } from './request/DeleteFleetConfigurationRequest';
import { DeleteFleetConfigurationResponse } from './responses/DeleteFleetConfigurationResponse';
import { DeleteCloudEnvironmentResponse } from './responses/DeleteCloudEnvironmentResponse';
import { DeleteCloudAccountResponse } from './responses/DeleteCloudAccountResponse';
import { DeleteCloudEnvironmentRequest } from './request/DeleteCloudEnvironmentRequest';
import { DeleteCloudAccountRequest } from './request/DeleteCloudAccountRequest';
import { CreateCloudEnvironmentRequest } from './request/CreateCloudEnvironmentRequest';
import { CreateCloudEnvironmentResponse } from './responses/CreateCloudEnvironmentResponse';
import { CreateCloudAccountRequest } from './request/CreateCloudAccountRequest';
import { CreateCloudAccountResponse } from './responses/CreateCloudAccountResponse';
import { GetDeploymentTypesResponse } from './responses/GetDeploymentTypesResponse';
import { GetSupportedRegionsResponse } from './responses/GetSupportedRegionsResponse';
import { GetDevOpsActionsAndParametersResponse } from './responses/GetDevOpsActionsAndParametersResponse';
import { GetDevOpsActionDetailsResponse } from './responses/GetDevOpsActionDetailsResponse';
import { GetDevOpsActionsRequest } from './request/GetDevOpsActionsRequest';
import { GetDevOpsActionsResponse } from './responses/GetDevOpsActionsResponse';
import { CreateDevOpsActionRequest } from './request/CreateDevOpsActionRequest';
import { CreateDevOpsActionResponse } from './responses/CreateDevOpsActionResponse';
import { DeleteDevOpsActionRequest } from './request/DeleteDevOpsActionRequest';
import { DeleteDevOpsActionResponse } from './responses/DeleteDevOpsActionResponse';
import { UpdateCloudEnvironmentRequest } from './request/UpdateCloudEnvironmentRequest';
import { UpdateCloudEnvironmentResponse } from './responses/UpdateCloudEnvironmentResponse';
import { GetAppStreamImagesResponse } from './responses/GetAppStreamImagesResponse';
import { GetEnvironmentVersionsResponse } from './responses/GetEnvironmentVersionsResponse';
import { GetFleetUsersResponse } from './responses/GetFleetUsersResponse';
import { DeleteFleetUserRequest } from './request/DeleteFleetUserRequest';
import { DeleteFleetUserResponse } from './responses/DeleteFleetUserResponse';
import { CreateFleetUserRequest } from './request/CreateFleetUserRequest';
import { CreateFleetUserResponse } from './responses/CreateFleetUserResponse';
import { UpdateFleetUserRequest } from './request/UpdateFleetUserRequest';
import { UpdateFleetUserResponse } from './responses/UpdateFleetUserResponse';

export class ErmDevOpsApiClientService {

  httpClient: HttpClient;

  constructor(config: IErmDevOpsApiClientServiceConfig, httpClientBuilder: ErmApiClientBuilderService) {
    this.httpClient = httpClientBuilder
      .useBaseUrl(config.baseUrl)
      .useLogging(config.enableLogging)
      .useAuthHeader(config.authHeaderResolver)
      .acceptJson()
      .build();
  }

  async getAlarms(): Promise<HttpResponse<GetAlarmsResponse>> {
    return this.httpClient.get<GetAlarmsResponse>('alarms', { observe: 'response' }).toPromise();
  }

  async getRegionUsage(): Promise<HttpResponse<GetRegionUsageResponse>> {
    return this.httpClient.get<GetRegionUsageResponse>('statistics/region-usage', { observe: 'response' }).toPromise();
  }

  async getCloudAccounts(): Promise<HttpResponse<GetCloudAccountsResponse>> {
    return this.httpClient.get<GetCloudAccountsResponse>('accounts', { observe: 'response' }).toPromise();
  }

  async getCloudAccount(accountId: string): Promise<HttpResponse<GetCloudAccountResponse>> {
    return this.httpClient.get<GetCloudAccountResponse>(`accounts/${accountId}`, { observe: 'response' }).toPromise();
  }

  async createCloudAccount(request: CreateCloudAccountRequest): Promise<HttpResponse<CreateCloudAccountResponse>> {
    return this.httpClient.post<CreateCloudAccountResponse>('accounts/create', request, { observe: 'response' }).toPromise();
  }

  async deleteCloudAccount(accountId: string): Promise<HttpResponse<DeleteCloudAccountResponse>> {
    const request = new DeleteCloudAccountRequest();
    request.accountId = accountId;
    return this.httpClient.post<DeleteCloudAccountResponse>('accounts/delete', request, { observe: 'response' }).toPromise();
  }

  async getCloudAccountAlarms(accountId: string): Promise<HttpResponse<GetCloudAccountAlarmsResponse>> {
    return this.httpClient.get<GetCloudAccountAlarmsResponse>(`accounts/${accountId}/alarms`, { observe: 'response' }).toPromise();
  }

  async getCloudCustomers(): Promise<HttpResponse<GetCloudCustomersResponse>> {
    return this.httpClient.get<GetCloudCustomersResponse>('customers', { observe: 'response' }).toPromise();
  }

  async getCloudCustomer(customerId: string): Promise<HttpResponse<GetCloudCustomerResponse>> {
    return this.httpClient.get<GetCloudCustomerResponse>(`customers/${customerId}`, { observe: 'response' }).toPromise();
  }

  async getCloudEnvironments(): Promise<HttpResponse<GetCloudEnvironmentsResponse>> {
    return this.httpClient.get<GetCloudEnvironmentsResponse>('environments', { observe: 'response' }).toPromise();
  }

  async getCloudEnvironment(environmentId: string): Promise<HttpResponse<GetCloudEnvironmentResponse>> {
    return this.httpClient.get<GetCloudEnvironmentResponse>(`environments/${environmentId}`, { observe: 'response' }).toPromise();
  }

  async createCloudEnvironment(request: CreateCloudEnvironmentRequest): Promise<HttpResponse<CreateCloudEnvironmentResponse>> {
    return this.httpClient.post<CreateCloudEnvironmentResponse>('environments/create', request, { observe: 'response' }).toPromise();
  }

  async updateCloudEnvironment(request: UpdateCloudEnvironmentRequest): Promise<HttpResponse<UpdateCloudEnvironmentResponse>> {
    return this.httpClient.post<UpdateCloudEnvironmentResponse>('environments/update', request, { observe: 'response' }).toPromise();
  }

  async deleteCloudEnvironment(environmentId: string): Promise<HttpResponse<DeleteCloudEnvironmentResponse>> {
    const request = new DeleteCloudEnvironmentRequest();
    request.environmentId = environmentId;
    return this.httpClient.post<object>('environments/delete', request, { observe: 'response' }).toPromise();
  }

  async getCloudEnvironmentAlarms(environmentId: string): Promise<HttpResponse<GetCloudEnvironmentAlarmsResponse>> {
    return this.httpClient.get<GetCloudEnvironmentAlarmsResponse>(`environments/${environmentId}/alarms`, { observe: 'response' }).toPromise();
  }

  async getCloudEnvironmentHealthChecks(environmentId: string): Promise<HttpResponse<GetCloudEnvironmentHealthChecksReponse>> {
    return this.httpClient.get<GetCloudEnvironmentHealthChecksReponse>(`environments/${environmentId}/health-checks`, { observe: 'response' }).toPromise();
  }

  async getCloudEnvironmentHealthCheckResult(environmentId: string, serviceName: string): Promise<HttpResponse<GetCloudEnvironmentHealthCheckResultResponse>> {
    environmentId = encodeURIComponent(environmentId);
    serviceName = encodeURIComponent(serviceName);
    return this.httpClient.get<GetCloudEnvironmentHealthCheckResultResponse>(`environments/${environmentId}/health-check-result/${serviceName}`, { observe: 'response' }).toPromise();
  }
  
  async getFleetDefinitionDefaults(): Promise<HttpResponse<GetFleetDefinitionDefaultsResponse>> {
    return this.httpClient.get<GetFleetDefinitionDefaultsResponse>(`fleet-definition/defaults`, { observe: 'response' }).toPromise();
  }
  
  async getFleetConstantClassCollection(): Promise<HttpResponse<GetFleetConstantClassCollectionResponse>> {
    return this.httpClient.get<GetFleetConstantClassCollectionResponse>(`lov/fleet-constant-class`, { observe: 'response' }).toPromise();
  }
  
  async getFleetDefinitions(accountId: string): Promise<HttpResponse<GetFleetDefinitionsResponse>> {
    return this.httpClient.get<GetFleetDefinitionsResponse>(`fleet-definition/list/${accountId}`, { observe: 'response' }).toPromise();
  }

  async createFleetDefinition(request: CreateFleetDefinitionRequest): Promise<HttpResponse<CreateFleetDefinitionResponse>> {
    return this.httpClient.post<CreateFleetDefinitionResponse>('fleet-definition/create', request, { observe: 'response' }).toPromise();
  }

  async deleteFleetDefinition(fleetId: string): Promise<HttpResponse<DeleteFleetDefinitionResponse>> {
    const request = new DeleteFleetDefinitionRequest();
    request.fleetId = fleetId;
    return this.httpClient.post<DeleteFleetDefinitionResponse>('fleet-definition/delete', request, { observe: 'response' }).toPromise();
  }

  async getFleetConfigurations(accountId: string, fleetId: string): Promise<HttpResponse<GetFleetConfigurationsResponse>> {
    accountId = encodeURIComponent(accountId);
    fleetId = encodeURIComponent(fleetId);
    return this.httpClient.get<GetFleetConfigurationsResponse>(`fleet-configuration/${accountId}/${fleetId}`, { observe: 'response' }).toPromise();
  }

  async updateFleetConfiguration(request: UpdateFleetConfigurationRequest): Promise<HttpResponse<UpdateFleetConfigurationResponse>> {
    return this.httpClient.put('fleet-configuration/update', request, { observe: 'response' }).toPromise();
  }

  async deleteFleetConfiguration(fleetId: string): Promise<HttpResponse<DeleteFleetConfigurationResponse>> {
    const request = new DeleteFleetConfigurationRequest();
    request.fleetId = fleetId;
    return this.httpClient.post<DeleteFleetConfigurationResponse>('fleet-configuration/delete', request, { observe: 'response' }).toPromise();
  }

  async getFleetUsers(customerId: string) : Promise<HttpResponse<GetFleetUsersResponse>> {
    customerId = encodeURIComponent(customerId);
    return this.httpClient.get<GetFleetUsersResponse>(`fleet-user/list/${customerId}`, { observe: 'response' }).toPromise();
  }

  async createFleetUser(request: CreateFleetUserRequest): Promise<HttpResponse<CreateFleetUserResponse>> {
    return this.httpClient.post<CreateFleetUserResponse>('fleet-user/create', request, { observe: 'response' }).toPromise();
  }

  async updateFleetUser(request: UpdateFleetUserRequest): Promise<HttpResponse<UpdateFleetUserResponse>> {
    return this.httpClient.post('fleet-user/update', request, { observe: 'response' }).toPromise();
  }

  async deleteFleetUser(request: DeleteFleetUserRequest) : Promise<HttpResponse<DeleteFleetUserResponse>> {
    return this.httpClient.post<DeleteFleetUserResponse>('fleet-user/delete', request, { observe: 'response' }).toPromise();
  }

  async getEnvironmentVersions(): Promise<HttpResponse<GetEnvironmentVersionsResponse>> {
    return this.httpClient.get<GetEnvironmentVersionsResponse>(`lov/environment-versions`, { observe: 'response' }).toPromise();
  }

  async getAppStreamImages(): Promise<HttpResponse<GetAppStreamImagesResponse>> {
    return this.httpClient.get<GetAppStreamImagesResponse>(`lov/appstream-images`, { observe: 'response' }).toPromise();
  }
  
  async getSupportedRegions(): Promise<HttpResponse<GetSupportedRegionsResponse>> {
    return this.httpClient.get<GetSupportedRegionsResponse>(`lov/regions`, { observe: 'response' }).toPromise();
  }

  async getDeploymentTypes(): Promise<HttpResponse<GetDeploymentTypesResponse>> {
    return this.httpClient.get<GetDeploymentTypesResponse>(`lov/deployment-types`, { observe: 'response' }).toPromise();
  }

  async getDevOpsActionsAndParameters(): Promise<HttpResponse<GetDevOpsActionsAndParametersResponse>> {
    return this.httpClient.get<GetDevOpsActionsAndParametersResponse>(`lov/devops-actions-parameters`, { observe: 'response' }).toPromise();
  }

  async getDevOpsActionDetails(eventRuleId: string, maxNextRunTimeStamps: number, maxLastRunIds: number): Promise<HttpResponse<GetDevOpsActionDetailsResponse>> {
    eventRuleId = encodeURIComponent(eventRuleId);
    return this.httpClient.get<GetDevOpsActionDetailsResponse>(`devops-action/${eventRuleId}/${maxNextRunTimeStamps}/${maxLastRunIds}`, { observe: 'response' }).toPromise();
  }

  async getDevOpsActions(request: GetDevOpsActionsRequest): Promise<HttpResponse<GetDevOpsActionsResponse>> {
    return this.httpClient.post<GetDevOpsActionsResponse>(`devops-action/list`, request, { observe: 'response' }).toPromise();
  }

  async createDevOpsAction(request: CreateDevOpsActionRequest): Promise<HttpResponse<CreateDevOpsActionResponse>> {
    return this.httpClient.post<CreateDevOpsActionResponse>(`devops-action/create`, request, { observe: 'response' }).toPromise();
  }

  async deleteDevOpsAction(eventRuleId: string): Promise<HttpResponse<DeleteDevOpsActionResponse>> {
    const request = new DeleteDevOpsActionRequest();
    request.eventRuleId = eventRuleId;
    return this.httpClient.post<DeleteDevOpsActionResponse>(`devops-action/delete`, request, { observe: 'response' }).toPromise();
  }

  // "await" should not be used redundantly (typescript:S4326) sonarQube
}
